import { sb } from '../supabase.ts';
import { escapeHtml } from '../main.ts';

export async function renderOtherSchoolDashboardPage(app: HTMLElement): Promise<void> {
  const { data: { session } } = await sb.auth.getSession();
  const userEmail = session?.user?.email || 'admin@fenimodel.school';

  // 1. Detect school name from local storage
  let schoolName = localStorage.getItem('adminButton') || '';
  
  // If the user arrived here via back button but is logged out, clear the URL
  if (!schoolName && window.location.search) {
    window.history.replaceState(null, '', '/other_school_dashboard');
  }

  // Rewrite URL state if we have a school operator to preserve the premium custom URL look
  if (schoolName && !window.location.search.includes('*' + schoolName)) {
    window.history.replaceState(null, '', `/other_school_dashboard?*${encodeURIComponent(schoolName)}`);
  }

  const displayName = schoolName ? schoolName.replace(/_/g, ' ') : 'Feni Model High School';
  const displayRole = schoolName ? 'School Admin' : 'Super Admin';
  const displayStatus = schoolName ? 'School Portal Connected' : 'External Database Connected';
  const isSchoolOperator = !!schoolName;

  // Define dynamic links and groups
  const databaseLinks = [
    {
      label: 'Student Database',
      href: '/other_school/database',
      icon: '📚',
      badge: '2026',
      badgeBg: '#059669', // tag green
      desc: 'Browse and search student directory, verify profiles and update statuses.'
    }
  ];

  // Only add login link if not already logged in as a school operator
  if (!isSchoolOperator) {
    databaseLinks.push({
      label: 'School Login (Others)',
      href: '/other_school/login',
      icon: '🔑',
      badge: 'LOGIN',
      badgeBg: '#ea580c', // tag orange
      desc: 'Authentication portal for other local school administrative staff.'
    });
  }

  const admissionLinks = [
    {
      label: 'New Admission',
      href: '/other_school/admission',
      icon: '📝',
      badge: 'ADMISSION',
      badgeBg: '#059669', // tag green
      desc: 'Add newly admitted student records into the external school registry.'
    },
    {
      label: 'Edit Admission Form',
      href: '/other_school/edit_application',
      icon: '✏️',
      badge: 'APPLICATIONS',
      badgeBg: '#2563eb', // tag blue
      desc: 'Review, modify and approve pending student admission application forms.'
    },
    {
      label: 'Download Form',
      href: '/other_school/download_form',
      icon: '⬇️',
      badge: 'FORMS',
      badgeBg: '#64748b', // tag slate
      desc: 'Export blank registration, exam or dynamic student information forms.'
    },
    {
      label: 'Transfer Certificate (TC)',
      href: '/other_school/tc/tc_edit_form',
      icon: '📄',
      badge: 'TC PORTAL',
      badgeBg: '#2563eb', // tag blue
      desc: 'Process and issue dynamic academic transfer certificates for students.'
    }
  ];

  const examLinks = [
    {
      label: 'Result Entry',
      href: '/other_school/result_entry',
      icon: '📊',
      badge: 'CLASS 6-8',
      badgeBg: '#7c3aed', // tag purple
      desc: 'Input term exams, assignments and continuous assessment scores for class 6-8.'
    },
    {
      label: 'Result Entry (9, 10)',
      href: '/other_school/result_entry_9',
      icon: '📊',
      badge: 'CLASS 9-10',
      badgeBg: '#4f46e5', // tag indigo
      desc: 'Enter board exam records and academic scorecard details for class 9-10.'
    },
    {
      label: 'Result (Others)',
      href: '/other_school/result/list',
      icon: '📋',
      badge: 'RESULTS',
      badgeBg: '#ea580c', // tag amber
      desc: 'Generate, print or export term academic marksheets for class 6-8.'
    },
    {
      label: 'Result (9-10)',
      href: '/other_school/result/list_9',
      icon: '📋',
      badge: 'RESULTS 9-10',
      badgeBg: '#dc2626', // tag red
      desc: 'Generate, print or export term academic marksheets for class 9-10.'
    }
  ];

  const dashboardGroups = [
    {
      title: 'Database Management',
      accentColor: '#3b82f6',
      groupClass: 'group-db-os',
      links: databaseLinks
    },
    {
      title: 'Registration & TC Utilities',
      accentColor: '#10b981',
      groupClass: 'group-exams-os',
      links: admissionLinks
    },
    {
      title: 'Examination Results & Processing',
      accentColor: '#f59e0b',
      groupClass: 'group-docs-os',
      links: examLinks
    }
  ];

  app.innerHTML = `
    <style>
      :root {
          --bg-os: #f5f7fb;
          --bg-alt-os: #ffffff;
          --text-os: #1f2d3d;
          --text-soft-os: #546170;
          --brand-os: #2563eb;
          --brand-accent-os: #1d4ed8;
          --radius-os: 18px;
          --border-os: #e2e8f0;
          --shadow-os: 0 6px 20px -2px rgba(0,0,0,.08),0 2px 6px -1px rgba(0,0,0,.04);
      }
      
      /* Header styles exactly matching fenimodel25 */
      header.site-head-os {
          position: sticky;
          top: 0;
          z-index: 20;
          padding: .75rem 1.25rem .75rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: .9rem;
          flex-wrap: wrap;
          background: rgba(15,23,42,.92);
          backdrop-filter: blur(14px);
          border-bottom: 1px solid rgba(148,163,184,.35);
          color: #e5e7eb;
          font-family: 'Kalpurush', 'Noto Sans Bengali', 'Hind Siliguri', 'Segoe UI', sans-serif;
      }
      .brand-lockup-os { display: flex; flex-direction: column; gap: .25rem; min-width: 0; }
      .brand-kicker-os {
          display: inline-flex;
          width: max-content;
          padding: .28rem .68rem;
          border-radius: 999px;
          background: rgba(59,130,246,.14);
          color: #dbeafe;
          border: 1px solid rgba(147,197,253,.28);
          font-size: .64rem;
          font-weight: 700;
          letter-spacing: 1.3px;
          text-transform: uppercase;
      }
      .brand-badge-os {
          font-size: .7rem;
          letter-spacing: .9px;
          padding: .28rem .65rem;
          border: 1px solid rgba(148,163,184,.6);
          border-radius: 999px;
          background: rgba(15,23,42,.9);
          color: #e5e7eb;
          text-transform: uppercase;
      }
      header.site-head-os h1 {
          font-size: clamp(2.1rem, 3vw, 2.6rem);
          margin: .25rem 0 0;
          font-weight: 800;
          letter-spacing: .6px;
          background: linear-gradient(120deg, #0ea5e9, #6366f1, #22c55e);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          text-decoration: none;
          line-height: 1.05;
      }

      /* Main container gradient backdrops */
      .os-dashboard-container {
          min-height: calc(100vh - 80px);
          padding: 2rem 3rem 4rem;
          box-sizing: border-box;
          background:
              radial-gradient(circle at 12% 14%, rgba(56,189,248,.18), transparent 26%),
              radial-gradient(circle at 88% 4%, rgba(99,102,241,.16), transparent 24%),
              radial-gradient(circle at 52% 100%, rgba(34,197,94,.10), transparent 32%),
              linear-gradient(180deg, #eef6ff 0%, #f8fafc 44%, #eef2ff 100%);
          font-family: 'Kalpurush', 'Noto Sans Bengali', 'Hind Siliguri', system-ui, sans-serif;
          -webkit-font-smoothing: antialiased;
      }

      /* Logout bar and dashboard copy */
      .logout-bar-os {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin: 0 0 2rem;
          padding: 1.2rem 1.5rem;
          background: rgba(255,255,255,.72);
          border: 1px solid var(--border-os);
          border-radius: 24px;
          box-shadow: var(--shadow-os);
          backdrop-filter: blur(10px);
      }
      .portal-copy-os { flex: 1 1 300px; min-width: 0; }
      .toolbar-kicker-os {
          display: inline-flex;
          padding: .3rem .7rem;
          border-radius: 999px;
          background: rgba(37,99,235,.12);
          color: var(--brand-accent-os);
          font-size: .62rem;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: .55rem;
      }
      .portal-copy-os h2 { margin: 0; font-size: 1.55rem; line-height: 1.15; letter-spacing: .3px; color: var(--text-os); font-weight: 800; }
      .portal-copy-os p { margin: .45rem 0 0; color: var(--text-soft-os); font-size: .84rem; line-height: 1.5; max-width: 70ch; }
      
      .toolbar-actions-os { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: .7rem; }
      .logout-btn-os {
          background: linear-gradient(90deg, #ef4444, #dc2626);
          color: #fff;
          border: none;
          padding: .75rem 1.4rem;
          border-radius: 12px;
          cursor: pointer;
          font-size: .85rem;
          font-weight: 700;
          letter-spacing: .5px;
          box-shadow: 0 4px 14px -2px rgba(239,68,68,.4);
          transition: .3s;
      }
      .logout-btn-os:hover { filter: brightness(1.05); transform: translateY(-2px); }

      /* Groups and bento box layouts */
      .year-group-os {
          position: relative;
          border: 1px solid var(--border-os);
          border-radius: 28px;
          padding: 1.5rem;
          backdrop-filter: blur(3px);
          margin-bottom: 2.5rem;
          box-shadow: var(--shadow-os);
      }
      .section-head-os {
          font-weight: 800 !important;
          color: var(--text-os);
          letter-spacing: .9px;
          margin: 0 0 1.2rem;
          font-size: 1.05rem;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: .55rem;
      }
      .section-head-os span.line-os {
          width: 38px;
          height: 2px;
          border-radius: 2px;
      }

      /* Specific group theme modifiers */
      .group-db-os {
          background: linear-gradient(135deg, rgba(14,165,233,.12), rgba(14,165,233,.04));
          border-color: rgba(14,165,233,.35);
      }
      .group-db-os .section-head-os span.line-os {
          background: linear-gradient(90deg, #0ea5e9, transparent);
      }
      
      .group-exams-os {
          background: linear-gradient(135deg, rgba(34,197,94,.12), rgba(34,197,94,.04));
          border-color: rgba(34,197,94,.35);
      }
      .group-exams-os .section-head-os span.line-os {
          background: linear-gradient(90deg, #22c55e, transparent);
      }
      
      .group-docs-os {
          background: linear-gradient(135deg, rgba(245,158,11,.15), rgba(245,158,11,.05));
          border-color: rgba(245,158,11,.35);
      }
      .group-docs-os .section-head-os span.line-os {
          background: linear-gradient(90deg, #f59e0b, transparent);
      }

      /* Grid and Cards */
      .grid-os {
          display: grid;
          gap: 1.2rem;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      }
      .card-link-os {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: .75rem;
          padding: 1.35rem 1.25rem 1.45rem;
          background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,255,255,.82));
          border: 1px solid var(--border-os);
          border-radius: 22px;
          text-decoration: none;
          color: var(--text-os);
          box-shadow: var(--shadow-os);
          overflow: hidden;
          min-height: 165px;
          box-sizing: border-box;
          transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease, background .22s ease;
      }
      .card-link-os:before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(145deg, rgba(37,99,235,.06), transparent 55%);
          opacity: .9;
          pointer-events: none;
          transition: .5s;
      }
      .card-link-os:hover:before {
          background: linear-gradient(145deg, rgba(37,99,235,.15), transparent 60%);
      }
      .card-link-os:hover {
          transform: translateY(-6px);
          box-shadow: 0 18px 30px -16px rgba(15,23,42,.35), 0 8px 16px -12px rgba(37,99,235,.2);
          border-color: rgba(37,99,235,.25);
      }
      
      .card-icon-os { font-size: 2.1rem; line-height: 1; margin-bottom: 0.25rem; }
      .card-title-os { font-size: 1.05rem; font-weight: 800; letter-spacing: .4px; color: var(--text-os); margin: 0 0 0.25rem 0; line-height: 1.3; }
      .card-desc-os { font-size: .8rem; color: var(--text-soft-os); line-height: 1.45; margin: 0; }
      
      .tag-os {
          position: absolute;
          top: .8rem;
          right: .8rem;
          font-size: .75rem;
          background: var(--brand-os);
          color: #fff;
          padding: .4rem .7rem .45rem;
          border-radius: 999px;
          letter-spacing: .65px;
          font-weight: 600;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          text-transform: uppercase;
      }
    </style>

    <!-- Top site header matching C:\\Users\\Gopals Diary\\Documents\\fenimodel25\\index.html header exactly -->
    <header class="site-head-os">
        <div class="brand-lockup-os">
            <span class="brand-kicker-os">Official digital portal</span>
            <h1>${escapeHtml(displayName)}</h1>
        </div>
        <span class="brand-badge-os">RESULT · Testimonial · Transfer Certificate</span>
    </header>

    <div class="os-dashboard-container">
        
        <!-- Quick access logout bar matching index.html structure -->
        <div class="logout-bar-os">
            <div class="portal-copy-os">
                <span class="toolbar-kicker-os">Quick access dashboard</span>
                <h2>Student Modules & Exam Modules</h2>
                <p>Logged in as: <strong style="color: var(--brand-accent-os);">${escapeHtml(isSchoolOperator ? displayName : userEmail)}</strong> (${escapeHtml(displayRole)})</p>
            </div>
            <div class="toolbar-actions-os">
                <button class="logout-btn-os" id="logoutBtn">Sign Out</button>
            </div>
        </div>

        <!-- Groups panels rendered exactly in the style of index.html -->
        ${dashboardGroups.map(group => `
          <div class="year-group-os ${group.groupClass}">
              <h3 class="section-head-os">
                  <span class="line-os"></span> ${group.title}
              </h3>
              <div class="grid-os">
                  ${group.links.map(link => {
                    const isLoginBtn = link.label === 'School Login (Others)';
                    const cardStyle = isLoginBtn ? `
                      background: linear-gradient(180deg, rgba(255,253,250,.98), rgba(255,251,245,.95));
                      border-color: rgba(249,115,22,.25);
                    ` : '';
                    
                    const tagStyle = isLoginBtn ? 'background: #ea580c;' : `background: ${link.badgeBg};`;

                    return `
                      <a href="${link.href}" class="card-link-os" style="${cardStyle}"
                         onmouseover="if(${isLoginBtn}){ this.style.borderColor='rgba(249,115,22,.5)'; this.style.boxShadow='0 18px 30px -16px rgba(249,115,22,.25)'; }"
                         onmouseout="if(${isLoginBtn}){ this.style.borderColor='rgba(249,115,22,.25)'; this.style.boxShadow='var(--shadow-os)'; }">
                          <span class="tag-os" style="${tagStyle}">${link.badge}</span>
                          <span class="card-icon-os">${link.icon || '🔗'}</span>
                          <div>
                              <h4 class="card-title-os">${link.label}</h4>
                              <p class="card-desc-os">${link.desc}</p>
                          </div>
                      </a>
                    `;
                  }).join('')}
              </div>
          </div>
        `).join('')}

    </div>
  `;

  const logoutBtn = document.getElementById('logoutBtn');
  if (logoutBtn) {
    logoutBtn.onclick = async () => {
      const wasSchool = !!localStorage.getItem('adminButton');
      localStorage.removeItem('adminButton');
      localStorage.removeItem('adminLoginAt');
      localStorage.removeItem('adminLastActivity');
      if (wasSchool) {
        window.location.href = '/other_school/login';
      } else {
        await sb.auth.signOut();
        window.location.href = '/login';
      }
    };
  }
}
